import { FormattedMessage, useIntl } from 'react-intl'

import LocationSVG from 'app/assets/icons/listing/location-gradient.svg'
import LinearGradientBorderContainer from 'app/components/base/LinearGradientBorderContainer'
import Stack from 'app/components/base/Stack'
import Text from 'app/components/base/Text'
import { IS_NATIVE_APP } from 'app/utils/constants/env.constants'
import { defaultCurrency } from 'app/utils/constants/intl.constants'

import {
  ListingShippingMethodInfo,
  ListingShippingScope
} from '../../helpers/shippingReturnRules.helpers'
import msg from '../ShippingReturnRules.messages'

import AddAddressButton from './AddAddressButton'
import ChangeAddressButton from './ChangeAddressButton'

interface ShippingBlockProps {
  country?: string
  currency?: string
  ownShippingCurrency?: string
  cost: EstimateRateModel
  shippingMethods: ListingShippingMethodInfo[]
  showAddressActions?: boolean
  isNoAddress?: boolean
  isSeveralAddresses?: boolean
  addressOptions?: { id?: string | number; address: string }[]
  calculateShippingCost?: (address: Partial<Address> | Address) => Promise<void>
}

const getScopeLabel = (
  scope: ListingShippingScope,
  formatMessage: ReturnType<typeof useIntl>['formatMessage']
) =>
  formatMessage(
    scope === 'domestic' ? msg.domesticShipping : msg.internationalShipping
  )

const getMethodLabel = (
  method: ListingShippingMethodInfo['method'],
  formatMessage: ReturnType<typeof useIntl>['formatMessage']
) =>
  formatMessage(
    method === 'own_shipping' ? msg.ownShipping : msg.integratedShipping
  )

const ShippingMethodRow: RFC<{
  method: ListingShippingMethodInfo
  showScopeLabel: boolean
  currency: string
  ownShippingCurrency: string
  cost: EstimateRateModel
  showIntegratedEstimate: boolean
}> = ({
  method,
  showScopeLabel,
  currency,
  ownShippingCurrency,
  cost,
  showIntegratedEstimate
}) => {
  const { formatNumber, formatMessage } = useIntl()

  const isOwnShipping = method.method === 'own_shipping'

  const ownPriceLabel =
    method.ownShippingPrice != null
      ? formatNumber(method.ownShippingPrice, {
          currency: ownShippingCurrency || defaultCurrency,
          style: 'currency'
        })
      : null

  const rateWithCurrency = formatNumber(cost.estimate_rate || 0, {
    currency: cost?.estimate_rate_currency || defaultCurrency,
    style: 'currency'
  })

  const approxPrice = formatNumber(cost.converted_estimate_rate || 0, {
    currency,
    style: 'currency'
  })

  const methodLabel = showScopeLabel
    ? formatMessage(
        { defaultMessage: '{scope} {method}' },
        {
          scope: getScopeLabel(method.scope, formatMessage),
          method: getMethodLabel(method.method, formatMessage)
        }
      )
    : getMethodLabel(method.method, formatMessage)

  const showOwnPrice = isOwnShipping && !method.isFree && !!ownPriceLabel
  const showEstimate =
    !isOwnShipping &&
    !method.isFree &&
    showIntegratedEstimate &&
    !!cost.estimate_rate

  return (
    <LinearGradientBorderContainer
      direction="row"
      borderRadius={1}
      sxContent={{
        justifyContent: 'space-between',
        alignItems: 'center',
        minHeight: 46,
        backgroundColor: 'background.lightGreen',
        padding: 1,
        px: 2
      }}
    >
      <Text variant="body2" sx={{ fontWeight: 500 }}>
        {methodLabel}
      </Text>

      {showOwnPrice ? (
        <Text
          variant="subtitle1"
          sx={{ lineHeight: IS_NATIVE_APP ? 18 : '14px' }}
        >
          {ownPriceLabel}
        </Text>
      ) : showEstimate ? (
        <Stack
          direction="column"
          sx={{ alignItems: 'flex-end', justifyContent: 'center' }}
        >
          <Text
            variant="subtitle1"
            sx={{ lineHeight: IS_NATIVE_APP ? 18 : '14px' }}
          >
            {formatMessage(
              { defaultMessage: 'from {cost}' },
              { cost: rateWithCurrency }
            )}
          </Text>

          {!!cost.converted_estimate_rate && (
            <Text variant="subtitle2" sx={{ color: 'text.secondary' }}>
              {formatMessage(
                { defaultMessage: 'approx {approxPrice}' },
                { approxPrice }
              )}
            </Text>
          )}
        </Stack>
      ) : (
        <Text>
          {method.isFree && formatMessage({ defaultMessage: 'Free' })}
        </Text>
      )}
    </LinearGradientBorderContainer>
  )
}

const collapseIdenticalIntegratedMethods = (
  methods: ListingShippingMethodInfo[]
): ListingShippingMethodInfo[] => {
  const [firstMethod] = methods

  if (
    !firstMethod ||
    methods.length < 2 ||
    !methods.every((method) => method.method === 'integrated')
  ) {
    return methods
  }

  return [
    {
      ...firstMethod,
      isFree: methods.every((method) => method.isFree)
    }
  ]
}

const ShippingBlock: RFC<ShippingBlockProps> = ({
  country,
  currency = defaultCurrency,
  ownShippingCurrency = defaultCurrency,
  cost,
  shippingMethods,
  showAddressActions,
  isNoAddress,
  isSeveralAddresses,
  addressOptions = [],
  calculateShippingCost
}) => {
  const displayMethods = collapseIdenticalIntegratedMethods(shippingMethods)
  const showScopeLabels = displayMethods.length > 1

  const showIntegratedEstimate = !shippingMethods.some(
    (method) => method.method === 'own_shipping' && !method.isFree
  )

  return (
    <Stack spacing={1}>
      <Stack spacing={1}>
        {displayMethods.map((method) => (
          <ShippingMethodRow
            key={method.scope}
            method={method}
            showScopeLabel={showScopeLabels}
            currency={currency}
            ownShippingCurrency={ownShippingCurrency}
            cost={cost}
            showIntegratedEstimate={showIntegratedEstimate}
          />
        ))}
      </Stack>

      {!!country && (
        <Stack
          direction="row"
          spacing={2}
          sx={{ alignItems: 'center', mt: 0.25 }}
        >
          <LocationSVG />

          <Text variant="body2" color="text.third">
            <Text
              variant="body2"
              component="span"
              sx={{ color: 'text.primary' }}
            >
              <FormattedMessage defaultMessage="Ships from:" />
            </Text>
            {` ${country}`}
          </Text>
        </Stack>
      )}

      {showAddressActions && (
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          {isNoAddress && (
            <>
              <Text variant="body2" color="text.third">
                <FormattedMessage defaultMessage="Add your delivery address to see available shipping options and prices." />
              </Text>

              {!!calculateShippingCost && (
                <AddAddressButton
                  calculateShippingCost={calculateShippingCost}
                />
              )}
            </>
          )}

          {isSeveralAddresses && !!calculateShippingCost && (
            <ChangeAddressButton
              addressOptions={addressOptions}
              calculateShippingCost={calculateShippingCost}
            />
          )}
        </Stack>
      )}
    </Stack>
  )
}

export default ShippingBlock
